/**
 * Company Branding Service
 * Handles Firestore operations for company branding
 */

import { doc, getDoc, setDoc, onSnapshot, serverTimestamp, Unsubscribe } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { CompanyBranding } from '@/types/branding.types';
import { logger } from "@/lib/logger";

const STORAGE_PATH = 'branding';

/**
 * Get company branding (one-time fetch)
 */
export async function getCompanyBranding(teamId: string): Promise<CompanyBranding | null> {
  try {
    // Try companyBranding first (new location)
    const brandingRef = doc(db, 'teams', teamId, 'settings', 'companyBranding');
    const brandingSnap = await getDoc(brandingRef);
    
    if (brandingSnap.exists()) {
      const data = brandingSnap.data();
      return {
        logoUrl: data.logoUrl || '',
        companyName: data.companyName || '',
        vatNumber: data.vatNumber || '',
        kvkNumber: data.kvkNumber || '',
        address: data.address || '',
        updatedAt: data.updatedAt?.toDate(),
        version: 1,
      };
    }
    
    // Fallback to old location (teams/{teamId}/settings/app)
    const appRef = doc(db, 'teams', teamId, 'settings', 'app');
    const appSnap = await getDoc(appRef);
    
    if (!appSnap.exists()) {
      // Return defaults if no document exists
      return {
        logoUrl: '',
        companyName: '',
        vatNumber: '',
        kvkNumber: '',
        address: '',
        updatedAt: undefined,
        version: 1,
      };
    }
    
    const data = appSnap.data();
    
    return {
      logoUrl: data.company?.logo?.url || '',
      companyName: data.company?.legalName || '',
      vatNumber: data.company?.vatNumber || '',
      kvkNumber: data.company?.kvkNumber || '',
      address: data.company?.address || '',
      updatedAt: data.audit?.updatedAt?.toDate(),
      version: 1,
    };
  } catch (error: unknown) {
    // Handle permission denied gracefully
    if (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied') {
      logger.warn('[BrandingService] Permission denied - returning defaults');
      return {
        logoUrl: '',
        companyName: '',
        vatNumber: '',
        kvkNumber: '',
        address: '',
        updatedAt: undefined,
        version: 1,
      };
    }
    
    logger.error('[BrandingService] Failed to get branding:', error);
    return null;
  }
}

/**
 * Watch company branding (real-time listener)
 */
export function watchCompanyBranding(
  teamId: string,
  callback: (branding: CompanyBranding | null) => void
): Unsubscribe {
  const docRef = doc(db, 'teams', teamId, 'settings', 'companyBranding');
  
  return onSnapshot(
    docRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        // Return defaults if document doesn't exist
        callback({
          logoUrl: '',
          companyName: '',
          vatNumber: '',
          kvkNumber: '',
          address: '',
          updatedAt: undefined,
          version: 1,
        });
        return;
      }
      
      const data = snapshot.data();
      
      const branding: CompanyBranding = {
        logoUrl: data.logoUrl || '',
        companyName: data.companyName || '',
        vatNumber: data.vatNumber || '',
        kvkNumber: data.kvkNumber || '',
        address: data.address || '',
        updatedAt: data.updatedAt?.toDate(),
        version: 1,
      };
      
      callback(branding);
    },
    (error: unknown) => {
      // Handle permission denied gracefully - return defaults
      if (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied') {
        logger.warn('[BrandingService] Permission denied - returning defaults');
        callback({
          logoUrl: '',
          companyName: '',
          vatNumber: '',
          kvkNumber: '',
          address: '',
          updatedAt: undefined,
          version: 1,
        });
        return;
      }
      
      logger.error('[BrandingService] Snapshot error:', error);
      callback(null);
    }
  );
}

/**
 * Update company branding
 */
export async function updateCompanyBranding(
  teamId: string,
  input: Partial<CompanyBranding>
): Promise<void> {
  try {
    const docRef = doc(db, 'teams', teamId, 'settings', 'companyBranding');
    
    // Merge with existing data
    const updated = {
      companyName: input.companyName,
      vatNumber: input.vatNumber,
      kvkNumber: input.kvkNumber,
      address: input.address,
      logoUrl: input.logoUrl,
      updatedAt: serverTimestamp(),
    };
    
    // Remove undefined values
    Object.keys(updated).forEach(key => {
      if (updated[key as keyof typeof updated] === undefined) {
        delete updated[key as keyof typeof updated];
      }
    });
    
    await setDoc(docRef, updated, { merge: true });
  } catch (error: unknown) {
    // Handle permission denied with user-friendly message
    if (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied') {
      throw new Error("You don't have access to update team settings.");
    }
    
    logger.error('[BrandingService] Failed to update branding:', error);
    throw error;
  }
}

/**
 * Upload company logo to Firebase Storage
 */
export async function uploadCompanyLogo(teamId: string, file: File): Promise<string> {
  try {
    const ext = file.name.split('.').pop() || 'png';
    const storagePath = `${STORAGE_PATH}/${teamId}/company-logo.${ext}`;
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    logger.error('[BrandingService] Failed to upload logo:', error);
    throw error;
  }
}

/**
 * Delete company logo from Firebase Storage
 */
export async function deleteCompanyLogo(teamId: string, storagePath?: string): Promise<void> {
  try {
    if (!storagePath) return;
    
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    logger.error('[BrandingService] Failed to delete logo:', error);
    // Don't throw - logo might already be deleted
  }
}
